// ─────────────────────────────────────────────────────────────────────────────
// Regra de deep link da RECUPERAÇÃO DE SENHA.
//
// O e-mail/SMS de "esqueci minha senha" manda um link web pronto, ex.:
//   https://orbitta-production.web.app/recovery-password?code=1174&username=10795851000121
//
// No navegador a RecoveryPasswordPage lê `code` + `username` dos queryParams e já
// abre o passo "definir nova senha". Depois que App Links / Universal Links entraram
// no ar, esse MESMO link passa a abrir o APP em vez do site — então o app precisa
// fazer exatamente o mesmo trabalho: cair na /recovery-password com os dois params.
//
// Este arquivo é puro (sem Angular/Capacitor), 100% testável: só reconhece o link e
// devolve os parâmetros normalizados. Quem navega é o DeepLinkService.
// ─────────────────────────────────────────────────────────────────────────────

import { DeepLinkPayload } from "./deeplink.types";

/** Rota interna do app que trata a recuperação de senha (ver app.routes.ts). */
export const RECOVERY_PASSWORD_ROUTE = "/recovery-password";

/** Primeiro segmento do path que identifica o link de recuperação. */
const RECOVERY_PASSWORD_SEGMENT = "recovery-password";

/** Nomes aceitos para o código de verificação (o backend manda `code`). */
const CODE_PARAM_KEYS = ["code", "codigo"];

/** Nomes aceitos para o usuário — os mesmos aliases que a página já aceita na web
 *  (`username`, `cpfCnpj`, `cpfcnpj`, `document`). A comparação é feita em minúsculas. */
const USERNAME_PARAM_KEYS = ["username", "cpfcnpj", "document", "documento"];

/** Parâmetros já extraídos e prontos para virar queryParams da rota. */
export interface RecoveryPasswordDeepLink {
  /** Código de verificação enviado no e-mail/SMS. */
  code: string;
  /** CPF/CNPJ (ou e-mail) dono da recuperação — normalizado pela própria página. */
  username: string;
}

/** true quando o link aponta para a recuperação de senha (com ou sem os parâmetros). */
export function isRecoveryPasswordDeepLink(payload: DeepLinkPayload): boolean {
  return payload.segments[0]?.toLowerCase() === RECOVERY_PASSWORD_SEGMENT;
}

/**
 * Extrai `code` + `username` de um deep link de recuperação de senha.
 * Retorna null quando o link não é de recuperação ou quando falta algum dos dois
 * parâmetros (nesse caso a página deve abrir no passo inicial, pedindo o CPF/CNPJ).
 */
export function matchRecoveryPasswordDeepLink(payload: DeepLinkPayload): RecoveryPasswordDeepLink | null {
  if (!isRecoveryPasswordDeepLink(payload)) {
    return null;
  }

  // Chaves em minúsculas: o link pode chegar como `?Code=` / `?cpfCnpj=` dependendo
  // de quem montou a URL (e-mail, SMS, encurtador).
  const params = new Map<string, string>();
  Object.entries(payload.params).forEach(([key, value]) => {
    params.set(key.toLowerCase(), value);
  });

  const code = pickParam(params, CODE_PARAM_KEYS);
  const username = pickParam(params, USERNAME_PARAM_KEYS);

  if (!code || !username) {
    return null;
  }

  return { code, username };
}

/** Primeiro valor não-vazio entre os aliases aceitos. */
function pickParam(params: Map<string, string>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) {
      return value;
    }
  }
  return undefined;
}

/** Mascara o CPF/CNPJ/e-mail para o log (o link carrega dado pessoal + código válido). */
export function maskRecoveryUsername(username: string): string {
  if (username.includes("@")) {
    const [user, domain] = username.split("@");
    return `${user.slice(0, 2)}***@${domain}`;
  }
  return username.length <= 4 ? "***" : `***${username.slice(-4)}`;
}
